import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { articles } from "@/data/articles";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, BookOpen, Sparkles } from "lucide-react";
import Seo, { articleJsonLd } from "@/components/Seo";

export function Blog() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
      <Seo
        title="Artikel zur Lactoseintoleranz"
        description="Fundierte Artikel zu Lactoseintoleranz - Mythen, Toleranzaufbau, Mikrobiom, Verdauung. Wissenschaftlich sauber, alltagstauglich geschrieben."
        path="/artikel"
        keywords="Lactoseintoleranz Artikel, Milchzucker Verdauung, Toleranzaufbau"
      />
      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Artikel</div>
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Fundiert. Klar. Alltagsnah.</h1>
      <p className="mt-4 text-slate-600 text-lg max-w-2xl">Aktuelle Beiträge rund um Lactoseintoleranz, Ernährung und Wohlbefinden.</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
        {articles.map((a) => (
          <Link key={a.slug} to={`/artikel/${a.slug}`} className="group" data-testid={`blog-card-${a.slug}`}>
            <ImagePlaceholder src={a.image} label="Artikelbild" ratio="aspect-[16/10]" className="mb-5" />
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{a.date} · {a.read}</div>
            <h3 className="font-semibold text-slate-900 text-lg leading-snug group-hover:text-emerald-700">{a.title}</h3>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">{a.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Article() {
  const { slug } = useParams();
  const idx = articles.findIndex((a) => a.slug === slug);
  if (idx < 0) return <Navigate to="/artikel" replace />;
  const article = articles[idx];
  const next = idx < articles.length - 1 ? articles[idx + 1] : null;
  const path = `/artikel/${slug}`;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
      <Seo
        title={article.title}
        description={article.excerpt}
        path={path}
        type="article"
        image={article.image?.startsWith("http") ? article.image : (article.image ? `https://laclive.de${article.image}` : undefined)}
        jsonLd={articleJsonLd(article, `https://laclive.de${path}`)}
      />
      <Link to="/artikel" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-emerald-700 mb-8" data-testid="article-back">
        <ArrowLeft className="w-4 h-4" /> Zur Übersicht
      </Link>
      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">{article.date} · {article.read}</div>
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight">{article.title}</h1>
      <ImagePlaceholder src={article.image} label="Artikelbild" ratio="aspect-[16/9]" className="my-10" />
      <div className="space-y-5 text-slate-700 text-lg leading-relaxed">
        {article.body.map((p, i) => <p key={i}>{p}</p>)}
      </div>

      {next ? (
        <Link to={`/artikel/${next.slug}`} className="block mt-16 group" data-testid="article-next">
          <div className="rounded-3xl border border-slate-100 hover:border-emerald-200 bg-gradient-to-br from-white to-slate-50 p-6 lg:p-8 card-lift flex items-center gap-5">
            <div className="hidden sm:block w-24 h-24 lg:w-32 lg:h-24 rounded-2xl overflow-hidden shrink-0 bg-slate-100">
              {next.image ? <img src={next.image} alt={next.title} loading="lazy" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Nächster Artikel
              </div>
              <h3 className="mt-1.5 text-lg lg:text-xl font-semibold text-slate-900 leading-snug group-hover:text-emerald-700 transition-colors">{next.title}</h3>
              <p className="hidden md:block text-sm text-slate-500 mt-1 line-clamp-2">{next.excerpt}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-emerald-700 shrink-0" />
          </div>
        </Link>
      ) : (
        <Link to="/paywall" className="block mt-16 group" data-testid="article-to-plan">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-100 p-8 lg:p-10 card-lift">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Du hast alle Artikel gelesen</div>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">Bereit für die praktische Umsetzung?</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">
                  Der strukturierte 12-Wochen-Plan mit Rezepten, klaren Mengenangaben und wöchentlicher Steigerung wartet - dauerhaft freigeschaltet mit einer Einmalzahlung.
                </p>
                <Button className="mt-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-5 btn-lift">
                  Zum 12-Wochen-Plan <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}
